import NotificationsIcon from "@mui/icons-material/Notifications";
import Box from "@mui/material/Box";
import type { ColumnDef } from "@tanstack/react-table";

import type { DriverRow } from "@/types/driver";
import ColumnHeader from "@/ui/ColumnHeader";
import type { DataTableFeatures } from "@/ui/dataTableFeatures";
import type { ColumnFilterOptions } from ".";
import { columnWidthFloors } from "./columnMinWidth";

/** Row-level feed notification indicator; shown in the leading driver-identifying column (name or promoted username). */
export function DriverFeedNotificationIcon({ feedPrefId }: { feedPrefId: string | null | undefined }) {
  return (
    <NotificationsIcon
      sx={{
        fontSize: "15px",
        flexShrink: 0,
        color: "#eab308",
        visibility: feedPrefId ? "visible" : "hidden",
      }}
    />
  );
}

function DriverNameCell({ driver, showNotificationBell }: { driver: DriverRow; showNotificationBell: boolean }) {
  return (
    <Box sx={{ display: "flex", width: "100%", alignItems: "center", gap: 1, minWidth: 0 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0, flex: 1, overflow: "hidden" }}>
        <Box sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{driver.name}</Box>
      </Box>
      {showNotificationBell ? <DriverFeedNotificationIcon feedPrefId={driver.feedPrefId} /> : null}
    </Box>
  );
}

export function driverNameColumn(
  options: ColumnFilterOptions,
  layout: { frozen: boolean; includeSort: boolean; includeNotificationBell: boolean } = {
    frozen: true,
    includeSort: true,
    includeNotificationBell: true,
  },
): ColumnDef<DataTableFeatures, DriverRow> {
  const { minSize, initialMinWidth } = columnWidthFloors("Driver", {
    draggable: !layout.frozen,
    hasFilter: layout.includeSort,
  });
  return {
    accessorKey: "name",
    header: () => (
      <ColumnHeader title="Driver" extra={layout.includeSort ? options.getColumnExtra("driver") : undefined} />
    ),
    cell: ({ row }) => <DriverNameCell driver={row.original} showNotificationBell={layout.includeNotificationBell} />,
    // When a leading username-tag column is the pinned identity, the name column is a normal movable center column;
    // otherwise it's the pinned (frozen) leading column.
    meta: layout.frozen ? { frozen: true, initialMinWidth } : { flex: 1.5, draggable: true, initialMinWidth },
    size: 215,
    minSize,
    enableSorting: false,
    enableGlobalFilter: false,
    enableHiding: false,
  };
}
